import { from, Observable, throwError as observableThrowError } from 'rxjs';

import { catchError, switchMap, tap } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { NotificationsService } from 'angular2-notifications';
import { AuthenticationService } from 'src/sb-tools/typescript/authentication/authentication.service';
import { Error } from 'src/sb-tools/typescript/enums/error.enum';
import { TranslateService } from '@ngx-translate/core';
import { AdminService } from '@shared/services/admin.service';
import { CommonDataFaqService } from './common-data-faq.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { environment } from '@environment';
import { LanguageService } from '@shared/services/language.service';

@Injectable()
export class Interceptor implements HttpInterceptor {
    constructor(
        private authenticationService: AuthenticationService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private commonDataFaqService: CommonDataFaqService,
        private adminService: AdminService,
        private router: Router,
        private location: Location,
        private languageService: LanguageService,
        private activatedRoute: ActivatedRoute,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let headers = req.headers.set('Version', environment.apiVersion);
        const isNeedAuthentication =
            req.url.toLowerCase().indexOf(environment.apiFaqUrl.toLowerCase() + 'admin') === 0 ||
            req.url.toLowerCase().indexOf(environment.apiMasterUrl.toLowerCase() + 'user') === 0;

        let clonedRequest = req.clone();

        if (req.url.toLowerCase().indexOf(environment.apiFaqUrl.toLowerCase()) === 0) {
            headers = headers
                .set('CustomerInstance', this.commonDataFaqService.communityName)
                .set('FAQInstance', this.commonDataFaqService.faqName)
                .set('SbLanguage', this.languageService.selectedLanguage?.toString() || '');
        } else if (
            req.url.toLowerCase().indexOf(environment.apiMasterUrl.toLowerCase()) === 0 ||
            req.url.toLowerCase().indexOf(environment.uploadsUrl.toLowerCase()) === 0
        ) {
            headers = headers.set('Community', this.commonDataFaqService.communityName);
        }

        if (this.activatedRoute.snapshot.queryParams.isPreview) {
            headers = headers.set('isPreview', 'true');
        }

        if (
            (req.url.toLowerCase().indexOf('oauth/token') === -1 &&
                (this.adminService.isAdmin || this.commonDataFaqService.faqSettings?.isProtectionEnabled)) ||
            (req.url.toLowerCase().indexOf('account/hasaccess') !== -1 &&
                this.authenticationService.isLoggedIn() &&
                this.isTokenForCurrentCustomer()) ||
            isNeedAuthentication
        ) {
            if (
                this.adminService.isAdmin ||
                req.url.toLowerCase().indexOf('account/hasaccess') !== -1 ||
                isNeedAuthentication ||
                (this.commonDataFaqService.faqSettings?.isProtectionEnabled && this.authenticationService.isLoggedIn())
            ) {
                return from(
                    this.authenticationService.getAuthorizationHeader(
                        this.commonDataFaqService.communityName,
                        !isNeedAuthentication,
                    ),
                ).pipe(
                    switchMap((data) => {
                        if (
                            data &&
                            data.status === 403 &&
                            [Error.accessDeniedToCommunity].includes(data.error.errorId) &&
                            this.commonDataFaqService.faqSettings?.isProtectionEnabled &&
                            this.authenticationService.isLoggedIn()
                        ) {
                            const url = this.location.path();

                            this.router.navigate(['unauthorized-faq-error'], {
                                skipLocationChange: true,
                            });

                            this.location.replaceState(url);
                        } else {
                            if (data) {
                                headers = headers.set('Authorization', data.toString());
                            } else {
                                if (!isNeedAuthentication) {
                                    this.adminService.isAdmin = false;

                                    headers = headers.set('Authorization', 'empty');
                                }
                            }

                            //   .append('Content-Type', application/json');
                            clonedRequest = clonedRequest.clone({
                                headers,
                            });

                            if (isNeedAuthentication) {
                                if (typeof data !== 'string') {
                                    return observableThrowError(data);
                                }

                                return next.handle(clonedRequest).pipe(
                                    tap((event) => {
                                        return this.setServerTime(event);
                                    }),
                                    catchError((response) => {
                                        return this.handleError(response, req, isNeedAuthentication);
                                    }),
                                );
                            } else {
                                // Pass the cloned request instead of the original request to the next handle
                                return next.handle(clonedRequest).pipe(
                                    catchError((response) => {
                                        return this.handleError(response, req, isNeedAuthentication);
                                    }),
                                );
                            }
                        }
                        // Pass the cloned request instead of the original request to the next handle
                    }),
                );
            } else if (
                this.commonDataFaqService.faqSettings?.isProtectionEnabled &&
                !this.authenticationService.isLoggedIn()
            ) {
                const url = this.location.path();

                this.router.navigate(['unauthorized-faq-error'], {
                    skipLocationChange: true,
                });

                this.location.replaceState(url);
            }
        }

        clonedRequest = clonedRequest.clone({
            headers,
        });

        // Pass the cloned request instead of the original request to the next handle
        return next.handle(clonedRequest).pipe(
            catchError((response) => {
                return this.handleError(response, clonedRequest, isNeedAuthentication);
            }),
        );
        // }
    }

    private handleError(response, req, isNeedAuthentication) {
        if (this.authenticationService.isLoggingOut) {
            return observableThrowError(response);
        }

        if (req.url.toLowerCase().indexOf('oauth/token') !== -1) {
            this.authenticationService.refreshPromise = null;
        }

        if (!response.error) {
            response.error = {};
        }

        if (isNeedAuthentication) {
            return this.handleErrorWhenNeedAuthentication(response, req);
        } else {
            return this.handleErrorWhenNoNeedAuthentication(response, req);
        }
    }

    private handleErrorWhenNeedAuthentication(response, req) {
        let message;
        if (response.status === 400 && req.url.toLowerCase().indexOf('oauth/token') !== -1) {
            if (response.error.errorId === Error.sessionExpired) {
                this.authenticationService.logOff({ isNeedRedirect: true });
                return observableThrowError(response);
            }

            if (response.error.errorId === Error.communityWithNameNotFound) {
                this.authenticationService.redirectToCommunities();
                return observableThrowError(response);
            }

            message = response.error.message || response.error.error_description;
            if (message) {
                this.notificationsService.error(this.translateService.instant('Error!'), message);
            }

            this.authenticationService.logOff({ isNeedRedirect: true });
        } else if (response.error && [Error.notEnoughPermissions].includes(response.error.errorId)) {
            const url = this.location.path();

            this.router.navigate(['403'], {
                skipLocationChange: true,
            });

            this.location.replaceState(url);
        } else if (response.status === 401) {
            this.authenticationService.needToRefreshToken = true;
        } else if (response.error && response.error.errorId === Error.communityWithNameNotFound) {
            window.location.pathname = '/404';
        } else {
            if (response.error && (typeof response.error === 'string' || response.error.message)) {
                message = response.error && response.error.message ? response.error.message : response.error;
                if (message.toLowerCase().indexOf('timeout') >= 0) {
                    message = this.translateService.instant(
                        'Oops... your action was not completed due to a slow Internet connection. Please wait for a few seconds and try again.',
                    );
                }
            } else {
                message = this.translateService.instant(
                    'Oops... Something went wrong, please try again or reload this page',
                );
            }

            this.notificationsService.error(this.translateService.instant('Error!'), message);
        }

        return observableThrowError(response);
    }

    private handleErrorWhenNoNeedAuthentication(response, req) {
        if (response.status === 403 && response.url.toLowerCase().indexOf('oauth/token') !== -1) {
            return observableThrowError(response);
        }

        if (response.status === 403 && response.error.errorId === Error.accessDenied) {
            const url = this.location.path();

            this.router.navigate(['unauthorized-faq-error'], {
                skipLocationChange: true,
            });

            this.location.replaceState(url);
        } else if (response.status !== 400 && response.status !== 404 && response.status !== -1) {
            let message: string;

            if (response.status !== 500 && response.error) {
                message = response.error.message || response.error.error_description;
            } else if (!(response.status === 0 && response.config && response.config.method === 'GET')) {
                message = this.translateService.instant(
                    'Oops...Something went wrong, please try again or reload this page',
                );
            }

            if (message) {
                this.notificationsService.error(this.translateService.instant('Error!'), message);
            }
        } else if (
            response.error &&
            [
                Error.communityWithNameNotFound,
                Error.communityNotFound,
                Error.faqInstanceNotFound,
                Error.categoryNotFound,
                Error.itemNotFound,
                Error.topicNotFound,
            ].includes(response.error.errorId)
        ) {
            const url = this.location.path();

            this.router.navigate(['404'], {
                skipLocationChange: true,
            });

            this.location.replaceState(url);
        } else if (response.status === 400 && req.url.toLowerCase().indexOf('oauth/token') !== -1) {
            this.adminService.logOff();
        }

        return observableThrowError(response);
    }

    // todo: whole logic of "hasAccess" should be rewritten with backend
    private isTokenForCurrentCustomer() {
        return this.commonDataFaqService.communityName.toLowerCase() === this.authenticationService.community;
    }

    private setServerTime(event) {
        if (
            event.type !== 0 &&
            event.url &&
            event.url.toLowerCase().indexOf(environment.apiMasterUrl.toLowerCase()) === 0
        ) {
            const serverTime = event.headers.get('date');
            this.authenticationService.setTimeDifference(serverTime);
        }
    }
}
